import React, { memo } from 'react';
import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import Badge from '@atlaskit/badge';
// eslint-disable-next-line jira/restricted/@atlassian+jira-common-styles
import { gridSize } from '@atlassian/jira-common-styles/src';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import messages from '../../../messages';
import EstimateFieldWrapper from '../../estimate-field-wrapper';

export type Props = {
	value: number | undefined;
	hasStoryPoints: boolean;
	shouldRenderStoryPointColumn: boolean;
};

const DEFAULT_NO_VALUE = '-';

export const StoryPointFieldStatic = ({
	value,
	hasStoryPoints,
	shouldRenderStoryPointColumn,
}: Props) => {
	const { formatNumber, formatMessage } = useIntl();

	if (ff('sea-2524-backlog-card-estimates-a11y-improvements_1ljqi')) {
		const ariaLabelWithValue = !isNil(value)
			? `${formatMessage(messages.storyPointsAriaLabel)}: ${formatNumber(value)}`
			: formatMessage(messages.storyPointsAriaLabelNullValue);

		return (
			<StoryPointWrapper shouldRenderStoryPointColumn={shouldRenderStoryPointColumn}>
				{hasStoryPoints && (
					<EstimateFieldWrapper
						tooltipLabel={formatMessage(messages.storyPointsTooltip)}
						ariaLabel={ariaLabelWithValue}
					>
						<StoryPointContainer>
							<Badge appearance="default">
								{!isNil(value) ? formatNumber(value) : DEFAULT_NO_VALUE}
							</Badge>
						</StoryPointContainer>
					</EstimateFieldWrapper>
				)}
			</StoryPointWrapper>
		);
	}

	return (
		<StoryPointWrapper shouldRenderStoryPointColumn={shouldRenderStoryPointColumn}>
			{hasStoryPoints && (
				<StoryPointContainer>
					<Badge appearance="default">
						{!isNil(value) ? formatNumber(value) : DEFAULT_NO_VALUE}
					</Badge>
				</StoryPointContainer>
			)}
		</StoryPointWrapper>
	);
};

export default memo<Props>(StoryPointFieldStatic);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StoryPointWrapper = styled.div<{ shouldRenderStoryPointColumn: boolean }>(
	{
		display: 'block',
		flexDirection: 'column',
		alignItems: 'center',
		boxSizing: 'border-box',
		// ensure styling remains consistent between rich and non-rich components
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		borderBottom: () =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			ff('jsw.backlog.improve-styling-issues-with-browser-font-size-settings')
				? undefined // replacing this with line height to ensure the element's perfectly vertically centered
				: // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
					`${gridSize / 4}px solid transparent`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
		lineHeight: () =>
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			ff('jsw.backlog.improve-styling-issues-with-browser-font-size-settings') ? 1 : undefined,
		whiteSpace: 'nowrap',
		cursor: 'default',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	({ shouldRenderStoryPointColumn }) =>
		shouldRenderStoryPointColumn && {
			overflow: 'inherit',
			width: 'fit-content',
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			minWidth: `${gridSize * 5.7}px`,
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
			maxWidth: `${gridSize * 5.7}px`,
			'&>span': {
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
				maxWidth: `${gridSize * 5.7}px`,
				display: 'block',
				overflow: 'hidden',
				whiteSpace: 'nowrap',
				textOverflow: 'ellipsis',
			},
		},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StoryPointContainer = styled.div({
	width: '100%',
});
